import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { message, Avatar, Tag } from 'antd';
import { getWorkWiseAuthToken } from '../../utils/auth';
import { workwiseServices, padServices, adminService } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { setIsAuthenticated, openCreatePadDrawer, closeCreatePadDrawer, setAdmins } from '../../app/EditorSlice';
import './styles/dark-theme.css';
import { useDarkMode } from '../DarkModeToggle';
import Sidebar from './components/Sidebar/Sidebar';
import Header from './components/Header/Header';
import CreatePadDrawer from './components/CreatePadDrawer/CreatePadDrawer';
import CreateFolderDrawer from './components/CreatePadDrawer/CreateFolderDrawer';
import ApplicationsDrawer from './components/ApplicationsDrawer/ApplicationsDrawer';
import CreateTemplateDrawer from './components/CreateTemplateDrawer/CreateTemplateDrawer';
import DocumentCard from './components/DocumentCard/DocumentCard';
import DocumentList from './components/DocumentList/DocumentListItem';
import DashboardSkeleton from './components/DashboardSkeleton';
import Spinner from '../../components/Spinner';
import UnauthorizedView from './components/UnauthorizedView/UnauthorizedView';
import WorkWsieIcon from '../../components/ResultComponent/WorkWsieIcon';
import TemplateGallery from './components/TemplateGallery';
import SyncLocalDocumentModal from './components/SyncLocalDocumentModal/SyncLocalDocumentModal';
import {
  getLocalDocuments,
  renameLocalDocument,
  removeLocalDocument,
  createLocalDocument,
  isLocalDocumentLimitReached,
  MAX_LOCAL_DOCUMENTS,
} from '../../utils/localDocuments';
import { getUserInfoFromToken } from '../../utils/auth';
import { isEffectivelyOffline } from '../../utils/helper';
import { useOfflineToggle } from '../OfflineToggle/useOfflineToggle';

export const TEMPLATE_CATEGORIES = [
  'Business',
  'Education',
  'Technology',
  'Marketing',
  'HR',
  'Finance',
  'Healthcare',
  'Design',
  'Legal',
  'Other',
];

const Dashboard = () => {
  useDarkMode({ applyTheme: true });
  const dispatch = useDispatch();
  const { offline } = useOfflineToggle();
  const isAuthenticated = useSelector(state => state.editor.isAuthenticated);
  const modals = useSelector(state => state.editor.modals);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSidebarItem, setActiveSidebarItem] = useState('all-documents');
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracks whether the very first (main) load has completed. First load shows the
  // full skeleton; every reload after shows a lightweight content-area spinner.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  // Sorting: each option provides sortBy (field) and sort (direction) numeric codes required by API
  const SORT_OPTIONS = [
    { id: 1, label: 'Create Date-Desc', sortBy: 1, sort: 2 },
    { id: 2, label: 'Create Date-Asc', sortBy: 2, sort: 1 },
    { id: 3, label: 'Name-Desc', sortBy: 3, sort: 2 },
    { id: 4, label: 'Name-Asc', sortBy: 4, sort: 1 },
    { id: 5, label: 'Update Date-Desc', sortBy: 5, sort: 2 },
    { id: 6, label: 'Update Date-Asc', sortBy: 6, sort: 1 },
  ];
  const [selectedSort, setSelectedSort] = useState(SORT_OPTIONS[0]); // Default to Update Date-Desc (most recent first)
  const [sortOpen, setSortOpen] = useState(false);
  const [myDrive, setMyDrive] = useState(false);
  const [appsDrawerOpen, setAppsDrawerOpen] = useState(false);
  const [folderDrawerOpen, setFolderDrawerOpen] = useState(false);
  const [folders, setFolders] = useState([]);
  const [parentId, setParentId] = useState('00000000-0000-0000-0000-000000000000');
  const [types, setTypes] = useState([6]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [adminsLoading, setAdminsLoading] = useState(true);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [createTemplateOpen, setCreateTemplateOpen] = useState(false);
  const [localDocs, setLocalDocs] = useState([]);
  const [syncDoc, setSyncDoc] = useState(null); // local doc pending sync, or null

  // Local (offline-created) documents live in localStorage.
  const loadLocalDocs = () => setLocalDocs(getLocalDocuments());

  const handleLocalRename = (id, name) => {
    renameLocalDocument(id, name);
    loadLocalDocs();
  };

  const handleLocalDelete = (id) => {
    removeLocalDocument(id);
    loadLocalDocs();
  };

  // Syncing creates a real server document, so it requires connectivity. Block it
  // (with a warning) while the user is still offline instead of opening the modal.
  const handleLocalSync = (localDoc) => {
    if (isEffectivelyOffline()) {
      message.warning('You are offline. Connect to the internet to sync this document.');
      return;
    }
    setSyncDoc(localDoc);
  };

  const handleLocalSynced = () => {
    loadLocalDocs();
    loadDocs(currentPage, pageSize, viewMode === 'grid'); // reflect the new server doc
  };

  // "Create Document" action. While offline we skip the Create Pad drawer entirely
  // and create a local document directly with a default name — there are no server
  // fields (privacy, collaborators) to fill in offline.
  const handleNewDocument = () => {
    if (!isEffectivelyOffline()) {
      dispatch(openCreatePadDrawer(null));
      return;
    }

    if (isLocalDocumentLimitReached()) {
      message.warning(
        `You already have ${MAX_LOCAL_DOCUMENTS} local documents. Delete one to create a new local document.`
      );
      return;
    }

    const user = getUserInfoFromToken();
    const creator = user
      ? { id: user.userId || user.id, name: user.name, image: user.image }
      : null;

    const record = createLocalDocument({ name: 'Untitled document', creator });
    if (!record) {
      message.warning(
        `You already have ${MAX_LOCAL_DOCUMENTS} local documents. Delete one to create a new local document.`
      );
      return;
    }

    loadLocalDocs();
    message.success('Local document created');
    window.open(`/${record.id}?local=true`, '_blank');
  };

  // Handler for Shared with me
  // Note: changing myDrive triggers the debounced load effect, so we must NOT
  // also call loadDocs here — a second direct fetch causes a spinner flicker.
  const handleSharedWithMe = () => {
    setMyDrive(true);
    setActiveSidebarItem('my-drive');
    resetPagination();
  };

  // Load admin list from API and save to global state
  const loadAdmins = async () => {
    if (isEffectivelyOffline()) {
      dispatch(setAdmins([]));
      setAdminsLoading(false);
      return;
    }
    setAdminsLoading(true);
    try {
      const adminRes = await adminService.getAllAdmins();
      const adminsData = Array.isArray(adminRes?.data?.data) ? adminRes.data.data : [];
      dispatch(setAdmins(adminsData));
      return adminsData;
    } catch (adminErr) {
      console.error('Failed to load admin templates:', adminErr);
      dispatch(setAdmins([]));
      return [];
    } finally {
      setAdminsLoading(false);
    }
  };

  // Handler for Signature document
  const handleSignatureDocument = () => {
    setActiveSidebarItem('signature');
    loadSignatureDocs();
  };

  // Changing myDrive/parentId/types triggers the debounced load effect, so these
  // branches only update state — calling loadDocs directly would double-fetch and
  // make the reload spinner flicker. Signature is the exception: it isn't an effect
  // dependency, so it must load explicitly.
  const handleActiveItemChange = (item) => {
    setActiveSidebarItem(item);
    if (item === 'all-documents') {
      setMyDrive(false);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([6]);
      resetPagination();
    } else if (item === 'signature') {
      loadSignatureDocs();
    } else if (item === 'my-drive') {
      setMyDrive(true);
      setParentId('00000000-0000-0000-0000-000000000000');
      setTypes([6]);
      resetPagination();
    } else {
      // Assume it's a folder id
      setMyDrive(false);
      setParentId(item);
      setTypes([]);
      resetPagination();
    }
  };

  // Load documents from API
  const loadDocs = async (page = 1, size = 10, loadAll = false) => {
    // Offline: there are no server documents to fetch. Skip the request so we
    // don't error out or trip the auth gate — local documents show regardless.
    if (isEffectivelyOffline()) {
      setDocs([]);
      setError('');
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }
    setLoading(true);
    setError('');
    // Only set activeSidebarItem if not viewing a specific folder
    if (parentId === '00000000-0000-0000-0000-000000000000') {
      setActiveSidebarItem(myDrive ? 'my-drive' : 'all-documents');
    }
    try {
      // Initialize sidebar state from cookie
      try {
        const cookieMatch = document.cookie.match(/(?:^|; )sidebar_state=([^;]+)/);
        if (cookieMatch) {
          const val = cookieMatch[1] === 'true';
          setSidebarOpen(val);
        }
      } catch { }

      const payload = {
        pageNo: loadAll ? 1 : page,
        pageSize: loadAll ? 50 : size, // Load many documents for grid view
        search, // 🔥 use search param
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId,
        myDrive,
        referenceType: 1,
        sortBy: selectedSort.sortBy,
        onlyFiles: false,
        types,
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setDocs(Array.isArray(res?.data?.data) ? res.data.data : []);
      dispatch(setIsAuthenticated(true));
    } catch (e) {
      dispatch(setIsAuthenticated(false));
      if (e.response?.status === 401) {
        dispatch(setIsAuthenticated(false));
      } else {
        setError(e?.message || 'Failed to load documents');
      }
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const handleRename = async (payload) => {
    try {
      await workwiseServices.updateDocumentName(payload);
      message.success('Document renamed successfully');
      await loadDocs(currentPage, pageSize, viewMode === 'grid'); // Reload to reflect changes
    } catch (e) {
      console.error('Failed to rename document:', e);
      const errorMessage = e.response?.data?.message || e.message || 'Failed to rename document. Please try again.';
      message.error(errorMessage);
      throw e; // Re-throw so DocumentCard can handle it
    }
  };

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
    loadDocs(page, size);
  };

  // Reset pagination when filters change
  const resetPagination = () => {
    setCurrentPage(1);
  };

  // Load signature documents from API
  const loadSignatureDocs = async () => {
    if (isEffectivelyOffline()) {
      setDocs([]);
      setError('');
      setLoading(false);
      setHasLoadedOnce(true);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        pageNo: 1,
        pageSize: 50,
        search: '',
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId: '00000000-0000-0000-0000-000000000000',
        myDrive: true,
        referenceType: 1,
        sortBy: 6,
        onlyFiles: false,
        types: [],
      };
      const res = await workwiseServices.getAllSignatureDocumentList(payload);
      setDocs(Array.isArray(res?.data?.data) ? res.data.data : []);
      dispatch(setIsAuthenticated(true));
    } catch (e) {
      if (e.response?.status === 401) {
        dispatch(setIsAuthenticated(false));
      } else {
        setError(e?.message || 'Failed to load signature documents');
      }
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  // Load folders from API
  const loadFolders = async () => {
    if (isEffectivelyOffline()) {
      setFolders([]);
      setFoldersLoading(false);
      return;
    }
    setFoldersLoading(true);
    try {
      const payload = {
        pageNo: 1,
        pageSize: 50,
        search: '',
        referenceId: '00000000-0000-0000-0000-000000000000',
        parentId: '00000000-0000-0000-0000-000000000000',
        myDrive: false,
        referenceType: 1,
        sortBy: 1,
        onlyFiles: false,
        types: [1],
      };
      const res = await workwiseServices.getAllDocumentList(payload);
      setFolders(Array.isArray(res?.data?.data) ? res.data.data : []);
      dispatch(setIsAuthenticated(true));
    } catch (e) {
      if (e.response?.status === 401) {
        dispatch(setIsAuthenticated(false));
      } else {
        console.error('Failed to load folders:', e);
        setFolders([]);
      }
    }
    finally {
      setFoldersLoading(false);
    }
  };

  // Load templates from API
  const loadTemplates = async () => {
    if (isEffectivelyOffline()) {
      setTemplates([]);
      setTemplatesLoading(false);
      return;
    }
    setTemplatesLoading(true);
    try {
      const res = await padServices.getAllTemplates();
      setTemplates(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (e) {
      console.error('Failed to load templates:', e);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Load documents from API
  useEffect(() => {
    let isMounted = true;

    async function load() {
      resetPagination();
      await loadDocs(1, pageSize, viewMode === 'grid'); // Load all for grid, paginated for list
    }

    // debounce to avoid firing API on every keystroke instantly
    const timeout = setTimeout(load, 400);

    return () => {
      isMounted = false;
      clearTimeout(timeout);
    };
  }, [search, selectedSort, myDrive, parentId, types, viewMode]);

  // Load folders on mount
  useEffect(() => {
    loadFolders();
  }, []);

  // Load templates on mount
  useEffect(() => {
    loadTemplates();
  }, []);

  // Load admins on mount
  useEffect(() => {
    loadAdmins();
  }, []);

  // When connectivity returns (offline → online, whether by real network recovery
  // or the manual work-offline toggle), refetch all server data instead of leaving
  // the user on the local-only view — and instead of flashing the unauthorized
  // view while isAuthenticated is still false. useLayoutEffect + an immediate
  // loading flag keep the skeleton/spinner on screen from the very next paint.
  const wasOfflineRef = useRef(offline);
  useLayoutEffect(() => {
    const cameOnline = wasOfflineRef.current && !offline;
    wasOfflineRef.current = offline;
    if (!cameOnline) return;

    setLoading(true);
    resetPagination();
    loadDocs(1, pageSize, viewMode === 'grid');
    loadFolders();
    loadTemplates();
    loadAdmins();
  }, [offline]);

  // Local documents: load on mount and refresh when a doc is created/edited in
  // another tab (storage event) or when the user returns to this tab (focus).
  useEffect(() => {
    loadLocalDocs();
    const onFocus = () => loadLocalDocs();
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === 'local_document_pad') loadLocalDocs();
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Persist sidebar state in cookie
  useEffect(() => {
    try {
      document.cookie = `sidebar_state=${sidebarOpen}; path=/; max-age=${60 * 60 * 24 * 7
        }`;
    } catch { }
  }, [sidebarOpen]);

  // Keyboard shortcut Ctrl/Cmd + B
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setSidebarOpen((s) => !s);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function openLoginWindow() {
    const width = 900; // Desired width in pixels
    const height = 600; // Desired height in pixels
    const url = "https://workw.com/login"; // Or any other URL you want to load
    const windowName = "Workwise Authentication";
    const left = (screen.width / 2) - (width / 2);
    const top = 50;

    // Define window features
    const features = `width=${width},height=${height},left=${left},top=${top},
                    toolbar=no,menubar=no,scrollbars=no,resizable=yes,
                    location=no,directories=no,status=no`;
    // const features = `width=${width},height=${height},resizable=yes,scrollbars=yes`;

    window.open(url, windowName, features);
  }
  const isDashboardLoading = loading || foldersLoading || templatesLoading || adminsLoading;
  // Full skeleton only on the initial main load; spinner for every reload after.
  const showSkeleton = isDashboardLoading && !hasLoadedOnce;
  const isReloading = loading && hasLoadedOnce;

  return (
    // Offline mode bypasses the auth gate entirely so the user can still reach
    // their local (offline-created) documents without a server session.
    !offline && !isDashboardLoading && !isAuthenticated ? (
      <div className="min-h-screen bg-gray-50 dashboard-zoom">
        <UnauthorizedView onLogin={openLoginWindow} />
      </div>
    ) : (
      <div className="min-h-screen bg-gray-50 dashboard-zoom">
        <DashboardSkeleton loading={showSkeleton} templateCount={8} documentCount={8}>
        <div className="flex">
        <AnimatePresence initial={false}>
          <motion.div
            key={sidebarOpen ? 'open' : 'closed'}
            initial={{ width: sidebarOpen ? '22rem' : '4rem' }}
            animate={{ width: sidebarOpen ? '22rem' : '4rem' }}
            exit={{ width: '4rem' }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="relative"
          >
            <Sidebar
              open={sidebarOpen}
              onToggle={() => setSidebarOpen((s) => !s)}
              search={search}
              onSearch={setSearch}
              onNewDocument={handleNewDocument}
              onSharedWithMe={handleSharedWithMe}
              onSignatureDocument={handleSignatureDocument}
              activeItem={activeSidebarItem}
              onActiveItemChange={handleActiveItemChange}
              onNewFolder={() => setFolderDrawerOpen(true)}
              folders={folders}
            />
          </motion.div>
        </AnimatePresence>
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-overlay md:hidden"
          />
        )}
        <main className="flex-1 min-w-0">
          <Header
            viewMode={viewMode}
            onToggleSidebar={() => {
              console.log('Hamburger clicked, opening drawer');
              setAppsDrawerOpen(true);
            }}
            onChangeView={setViewMode}
            sortDropdownOpen={sortOpen}
            onToggleSort={() => setSortOpen(o => !o)}
            sortOptions={SORT_OPTIONS}
            selectedSort={selectedSort}
            onSelectSort={(opt) => { setSelectedSort(opt); setSortOpen(false); }}
          />

          <CreatePadDrawer
            open={modals.createPad}
            onClose={() => dispatch(closeCreatePadDrawer())}
            onCreate={() => { dispatch(closeCreatePadDrawer()); loadDocs(currentPage, pageSize, viewMode === 'grid'); }}
          />

          <CreateFolderDrawer
            open={folderDrawerOpen}
            onClose={() => setFolderDrawerOpen(false)}
            onCreate={() => loadDocs(currentPage, pageSize, viewMode === 'grid')}
          />

          <ApplicationsDrawer
            open={appsDrawerOpen}
            onClose={() => setAppsDrawerOpen(false)}
          />

          <SyncLocalDocumentModal
            open={!!syncDoc}
            doc={syncDoc}
            onClose={() => setSyncDoc(null)}
            onSynced={handleLocalSynced}
          />

          <div className="px-6 lg:px-8 mt-4">

              {isReloading && (
                <Spinner label="Loading documents…" />
              )}

              {!isReloading && error && (
                <div className="py-12">
                  <div className="mx-auto max-w-lg rounded-md border border-red-200 bg-red-50 p-4 text-red-700">
                    {error}
                  </div>
                </div>
              )}

              {!isReloading && !error && (
                <>
                  {/* Templates Gallery — hidden offline: templates are server-backed
                      and offline documents always start blank, so they don't apply. */}
                  {!offline && (
                    <>
                      <TemplateGallery
                        templates={templates}
                        onCreateBlank={() => dispatch(openCreatePadDrawer(null))}
                        onOpenTemplate={(template) => dispatch(openCreatePadDrawer(template))}
                        onCreateTemplate={() => setCreateTemplateOpen(true)}
                      />
                      <CreateTemplateDrawer
                        open={createTemplateOpen}
                        onClose={() => setCreateTemplateOpen(false)}
                        onCreate={() => { setCreateTemplateOpen(false); loadTemplates(); }}
                      />
                    </>
                  )}

                  {/* Local (offline-created) documents */}
                  {localDocs.length > 0 && (
                    <>
                      <h2 className="text-[1.12rem] font-semibold text-gray-900 mb-4">Local documents</h2>
                      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-10">
                        {localDocs.map((doc, i) => (
                          <motion.div
                            key={doc.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                          >
                            <motion.div
                              whileHover={{ y: -4 }}
                              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            >
                              <DocumentCard
                                doc={doc}
                                isLocal
                                onLocalRename={handleLocalRename}
                                onDelete={handleLocalDelete}
                                onSync={handleLocalSync}
                              />
                            </motion.div>
                          </motion.div>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Recent Documents — always shown. While offline, server docs are
                      dimmed and warn on click (handled per-card). */}
                  <h2 className="text-[1.12rem] font-semibold text-gray-900 mb-4">Recent documents</h2>
                  {viewMode === 'grid' ? (
                    <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-10">
                      {docs.filter(doc => doc.documentType === 6).map((doc, i) => (
                        <motion.div
                          key={doc.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: i * 0.03 }}
                        >
                          <motion.div
                            whileHover={{ y: -4 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          >
                            <DocumentCard doc={doc} offline={offline} onRename={handleRename} />
                          </motion.div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="pb-10">
                      <DocumentList
                        docs={docs.filter(doc => doc.documentType === 6)}
                        offline={offline}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPaginationChange={handlePaginationChange}
                      />
                    </div>
                  )}
                  {docs.filter(doc => doc.documentType === 6).length === 0 && (
                    <div className="w-full max-w-3xl mx-auto flex flex-col items-center justify-center px-6 py-12 mb-10 text-center">
                      <div className={`flex items-center justify-center w-14 h-14 rounded-full mb-4 ${offline ? 'bg-amber-50 text-amber-500' : 'bg-gray-100 text-gray-400'}`}>
                        {offline ? (
                          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 1l22 22" />
                            <path d="M16.72 11.06A10.94 10.94 0 0119 12.55" />
                            <path d="M5 12.55a10.94 10.94 0 015.17-2.39" />
                            <path d="M10.71 5.05A16 16 0 0122.58 9" />
                            <path d="M1.42 9a15.91 15.91 0 014.7-2.88" />
                            <path d="M8.53 16.11a6 6 0 016.95 0" />
                            <line x1="12" y1="20" x2="12.01" y2="20" />
                          </svg>
                        ) : (
                          <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                            <path d="M14 2v6h6" />
                            <line x1="9" y1="13" x2="15" y2="13" />
                            <line x1="9" y1="17" x2="13" y2="17" />
                          </svg>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-800">
                        {offline ? "You're offline" : 'No documents found'}
                      </p>
                      <p className="mt-1 text-sm text-gray-400 max-w-xs">
                        {offline
                          ? 'Connect to the internet to see your recent documents.'
                          : 'Create a new document to get started.'}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
        </main>
        </div>
        </DashboardSkeleton>
      </div>
    )
  );
};

export default Dashboard;